import type { Message } from '@/types';
import type {
  MapleMythosPhase,
  MapleMythosReasoningMode,
  MapleMythosRunState,
} from './types';

const AUTO_TERMINAL_STEP_LIMIT = 8;

const reasoningModeInstructions: Record<MapleMythosReasoningMode, string> = {
  balanced: 'Balance speed and depth. Prefer clear decomposition, pragmatic criticism, and concise refinements.',
  skeptical: 'Be adversarial toward weak assumptions. Look for contradictions, missing evidence, and unstated constraints before refining.',
  expansive: 'Explore more alternatives and deeper subproblems before converging, but still stay grounded in the task.',
};

function trimSection(value: string, maxLength: number) {
  if (!value) return 'None yet.';
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

function buildWorkingStateSummary(run: MapleMythosRunState) {
  return [
    `Latest plan: ${trimSection(run.workingState.latestPlan, 1400)}`,
    `Latest critique: ${trimSection(run.workingState.latestCritique, 1400)}`,
    `Latest answer: ${trimSection(run.workingState.latestAnswer, 1800)}`,
    `Converged: ${run.workingState.converged ? 'yes' : 'no'}`,
    `Confidence: ${run.workingState.confidence}`,
  ].join('\n\n');
}

function buildToolContext(run: MapleMythosRunState) {
  const recentToolEntries = run.transcript
    .filter((entry) => entry.kind === 'tool')
    .slice(-6)
    .map((entry) => {
      const header = [entry.phase || 'tool', typeof entry.loopIndex === 'number' ? `loop ${entry.loopIndex}` : null]
        .filter(Boolean)
        .join(' · ');

      return [
        header || 'tool',
        trimSection(entry.message, 300),
        trimSection(entry.content || '', 2200),
      ]
        .filter(Boolean)
        .join('\n');
    });

  if (!recentToolEntries.length) {
    return 'No MapleOS terminal tool output yet.';
  }

  return recentToolEntries.join('\n\n---\n\n');
}

function buildSystemPrompt(run: MapleMythosRunState) {
  const sshInstruction = run.sshGrant
    ? [
        `Operator granted MapleOS SSH access through the saved profile "${run.sshGrant.name}" for ${run.sshGrant.username}@${run.sshGrant.host}:${run.sshGrant.port}.`,
        `If terminal work would help, you may emit exactly one fenced \`\`\`terminal block with up to ${AUTO_TERMINAL_STEP_LIMIT} MapleOS Terminal commands, one raw command per non-empty line.`,
        `To use the granted server, connect with \`ssh ${run.sshGrant.name}\` from MapleOS only when the SSH session is not already open.`,
        'After MapleOS connects, later terminal commands run inside that remote shell already. Do not keep prefixing commands with ssh profile-name once connected; just run the remote shell commands directly.',
        'Saved MapleOS SSH profile names are MapleOS conveniences, not remote-shell host aliases. Inside the remote shell, only use ssh again if you intentionally pivot to another host by real hostname or IP.',
        'If you need to return from the remote shell to MapleOS Terminal, use exit and wait for the SSH session to close before issuing local MapleOS-only commands.',
        'Only emit terminal blocks for commands you want MapleOS to execute. Use plain text for analysis that should not execute.',
        'After MapleOS tool output appears in the run transcript, continue from that output instead of repeating the same command.',
        'Never claim a file, directory, or command result exists unless the transcript already showed it exists or your command explicitly creates it.',
        'If you want to inspect an artifact such as modules.txt, first verify it with commands like pwd, ls, find, or test -e in the same terminal block or after a transcript entry proving it exists.',
        'If a discovery command reports that a run directory or file is missing, do not issue follow-up commands that assume it exists. Explain the missing prerequisite and choose a safer next step.',
        'Distinguish a real project or workspace from generated output. If the transcript shows repository or app markers such as README files, package manifests, build files, source directories, config files, or a normal checked-out project structure, treat that path as the primary workspace and inspect files in place instead of expecting generated artifacts there.',
        'Generated artifacts, logs, reports, exports, and temporary files should only be referenced after the transcript shows a tool created them or a verification command located them.',
      ].join(' ')
    : 'No SSH profile has been granted for this run. Do not assume remote server access.';

  return [
    'You are DeMythos, a looped reasoning engine running inside MapleOS.',
    'You are not in a chat. You are one phase in a planner → critic → refiner loop.',
    'Return useful, compact, structured text without conversational filler.',
    reasoningModeInstructions[run.reasoningMode],
    'Respect the user prompt and accumulated working state.',
    sshInstruction,
  ].join(' ');
}

function buildPhaseInstruction(phase: MapleMythosPhase, run: MapleMythosRunState) {
  const loopLabel = `Loop ${run.currentLoop}/${run.maxLoops}`;

  switch (phase) {
    case 'planner':
      return [
        `${loopLabel}. Produce the next best decomposition or attack plan for the task.`,
        'Focus on what should be solved next, not on polished final prose.',
        'Use concise bullets or short sections.',
        run.sshGrant ? 'If remote server work is required, you may include one fenced ```terminal block.' : null,
        run.sshGrant ? 'Any terminal plan that references a remote artifact must verify the path before trying to read it.' : null,
        run.sshGrant ? 'If the transcript already shows a source repository, prefer repo-inspection commands such as pwd, ls, find, rg, sed, or cat on real source files over guesses about generated report files.' : null,
      ].filter(Boolean).join(' ');
    case 'critic':
      return [
        `${loopLabel}. Critique the current plan and answer draft.`,
        'Flag wrong assumptions, contradictions, missing evidence, scope drift, or unsafe leaps.',
        'Prefer actionable criticism over generic skepticism.',
        'Use the recent MapleOS terminal tool output when judging whether the task is actually progressing.',
        'Call out when the plan confuses a source tree with generated output or assumes files that were never observed.',
      ].join(' ');
    case 'refiner':
      return [
        `${loopLabel}. Refine the working answer using the plan, critique, and any MapleOS terminal tool output.`,
        'Format your reply exactly like this:',
        'CONVERGED: yes|no',
        'CONFIDENCE: 0-100',
        'SUMMARY: one short paragraph',
        'ANSWER:',
        'Then provide the refined answer body.',
        'Treat missing files, empty search results, and absent directories as real findings rather than obstacles to hand-wave past.',
        'If the transcript shows a real project or workspace, continue by inspecting that workspace itself instead of inventing sidecar report artifacts.',

        run.sshGrant ? `If more remote execution is needed, you may also include one fenced \`\`\`terminal block with up to ${AUTO_TERMINAL_STEP_LIMIT} commands after the ANSWER section.` : '',
      ].join('\n');
    case 'coda':
      return [
        'Synthesize the final answer for the user from the completed reasoning state.',
        'Format your reply exactly like this:',
        'SUMMARY: one short paragraph',
        'FINAL ANSWER:',
        'Then provide the final answer body.',
      ].join('\n');
    default:
      return 'Advance the DeMythos reasoning state.';
  }
}

export function buildPhaseMessages(phase: MapleMythosPhase, run: MapleMythosRunState): Message[] {
  const latestReflection = run.workingState.reflections.at(-1);
  const reflectionText = latestReflection
    ? [
        `Previous planner output:\n${trimSection(latestReflection.planner, 1800)}`,
        `Previous critic output:\n${trimSection(latestReflection.critic, 1800)}`,
        `Previous refiner output:\n${trimSection(latestReflection.refiner, 2200)}`,
      ].join('\n\n')
    : 'No previous loop outputs yet.';

  return [
    {
      id: `maplemythos-system-${phase}`,
      chatId: run.id,
      role: 'system',
      sender: 'DeMythos',
      timestamp: new Date().toISOString(),
      content: buildSystemPrompt(run),
    },
    {
      id: `maplemythos-user-${phase}`,
      chatId: run.id,
      role: 'user',
      sender: 'DeMythos',
      timestamp: new Date().toISOString(),
      content: [
        `User task:\n${run.prompt}`,
        `Phase:\n${phase}`,
        `Current working state:\n${buildWorkingStateSummary(run)}`,
        `Latest loop reflection:\n${reflectionText}`,
        `Recent MapleOS terminal tool output:\n${buildToolContext(run)}`,
        buildPhaseInstruction(phase, run),
      ].join('\n\n'),
    },
  ];
}

export function parseRefinerOutput(content: string) {
  const convergedMatch = content.match(/CONVERGED:\s*(yes|no)/i);
  const confidenceMatch = content.match(/CONFIDENCE:\s*(\d{1,3})/i);
  const summaryMatch = content.match(/SUMMARY:\s*([\s\S]*?)(?:\nANSWER:|$)/i);
  const answerMatch = content.match(/ANSWER:\s*([\s\S]*)$/i);

  return {
    converged: convergedMatch?.[1]?.toLowerCase() === 'yes',
    confidence: Math.max(0, Math.min(100, Number(confidenceMatch?.[1] || 0))),
    summary: summaryMatch?.[1]?.trim() || '',
    answer: answerMatch?.[1]?.trim() || content.trim(),
  };
}

export function parseCodaOutput(content: string) {
  const summaryMatch = content.match(/SUMMARY:\s*([\s\S]*?)(?:\nFINAL ANSWER:|$)/i);
  const answerMatch = content.match(/FINAL ANSWER:\s*([\s\S]*)$/i);

  return {
    summary: summaryMatch?.[1]?.trim() || '',
    finalAnswer: answerMatch?.[1]?.trim() || content.trim(),
  };
}